import { useEffect, useState } from 'react';

import { getMyPermissions } from '@/pages/AccountSettings/api';
import type { IMyPermissionsResponse } from '@/pages/AccountSettings/types';

/**
 * Permission bit indices - must match backend Permissions.py
 */
export enum Permission {
  MANAGE_TEAM_MEMBERS = 0,
  MANAGE_ACCOUNT_SETTINGS = 1,
  VIEW_STRUCTURES = 2,
  MANAGE_STRUCTURES = 3,
  VIEW_UNITS = 4,
  MANAGE_UNITS = 5,
  VIEW_TENANTS = 6,
  MANAGE_TENANTS = 7,
  MANAGE_TENANT_ALERTS = 8,
  VIEW_DEVICES = 9,
  MANAGE_DEVICES = 10,
  VIEW_USAGE = 11,
  VIEW_BILLING = 12,
  MANAGE_BILLING = 13,
  EXPORT_DATA = 14,
}

/**
 * Hook to get current user's permissions and role
 */
export function useUserPermissions() {
  const [permissions, setPermissions] = useState<IMyPermissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    getMyPermissions()
      .then((response) => {
        setPermissions(response);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load user permissions:', err);
        setError(err);
        setLoading(false);
      });
  }, []);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: Permission): boolean => {
    if (!permissions) return false;
    return (permissions.permissions & (1 << permission)) !== 0;
  };

  /**
   * Check if user has ALL of the specified permissions
   */
  const hasAllPermissions = (requiredPermissions: Permission[]): boolean => {
    return requiredPermissions.every((p) => hasPermission(p));
  };

  /**
   * Check if user has ANY of the specified permissions
   */
  const hasAnyPermission = (requiredPermissions: Permission[]): boolean => {
    return requiredPermissions.some((p) => hasPermission(p));
  };

  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    roleKey: permissions?.role_key,
    isBillingManager: permissions?.role_key === 'billing_manager',
    isOwner: permissions?.role_key === 'owner',
    isAdmin: permissions?.role_key === 'admin',
  };
}
